import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { logger } from "@/utils/logger";
import {
  AlertTriangle,
  Info,
  Search,
  ArrowUpDown,
  Clock,
  Code,
  Volume2,
  Briefcase,
  Blocks,
  Contact,
  CircleFadingArrowUp,
  Clipboard,
  ListFilter,
  Trash2,
} from "lucide-react";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";
import { SimpleTable } from "@/components/SimpleTable";
import { ColumnDef, PaginationState, Updater } from "@tanstack/react-table";
import { useDebounce } from "@/hooks/useDebounce";
import {
  MissingArticleItemSchema,
  UserAddedMissingArticlePayload,
  UserStatus,
  PipelineStatus,
  POSSIBLE_USER_STATUSES,
  POSSIBLE_PIPELINE_STATUSES,
  MissingArticlesProps,
  PaginatedMissingArticles,
  UserAddedMissingArticle,
} from "@/types/missing_articles";

// --- localStorage Keys ---
const LS_SORT_BY_KEY = "missingArticlesListSortBy";
const LS_SORT_DESC_KEY = "missingArticlesListSortDesc";
const LS_SEARCH_TERM_KEY = "missingArticlesListSearchTerm";
const LS_USER_STATUS_FILTERS_KEY = "missingArticlesListUserStatusFilters";
const LS_PIPELINE_STATUS_FILTERS_KEY =
  "missingArticlesListPipelineStatusFilters";
// --- End localStorage Keys ---

// --- Section Icon Mapping ---
const sectionIconMap: Record<string, React.ReactElement> = {
  api: <Code className="h-4 w-4" />,
  "allmänna uppdateringar": <Clipboard className="h-4 w-4" />,
  allmänt: <Clipboard className="h-4 w-4" />,
  "förbättringar för arbetsgivaren": (
    <CircleFadingArrowUp className="h-4 w-4" />
  ),
  integrationer: <Blocks className="h-4 w-4" />,
  kbr: <Contact className="h-4 w-4" />,
  volymrekrytering: <Volume2 className="h-4 w-4" />,
  ärenden: <Briefcase className="h-4 w-4" />,
  "ärendespecifika uppdateringar": <Briefcase className="h-4 w-4" />,
  övrigt: <Clipboard className="h-4 w-4" />,
};

// --- Status Badge Colors ---
const getUserStatusColor = (status: UserStatus | string) => {
  switch (status) {
    case "Open":
      return "badge-neutral";
    case "Completed":
      return "badge-success";
    case "Investigating":
      return "badge-info";
    case "Not Relevant":
      return "badge-ghost";
    case "Duplicate":
      return "badge-warning";
    case "AI Generated":
      return "badge-primary";
    default:
      return "badge-outline";
  }
};

const getPipelineStatusColor = (status: PipelineStatus | string) => {
  switch (status) {
    case "Needs Article":
      return "badge-error";
    case "Processing":
      return "badge-warning";
    case "Implemented":
      return "badge-success";
    case "Manually Resolved":
      return "badge-info";
    case "Archived":
      return "badge-ghost";
    case "AI Generated":
      return "badge-primary";
    default:
      return "badge-outline";
  }
};

// --- Column Definition Factory ---
interface GetColumnsProps {
  sortBy: string;
  sortDesc: boolean;
  setSortBy: (sortBy: string) => void;
  setSortDesc: (sortDesc: boolean) => void;
  setPage: (page: number) => void; // For resetting page on sort
  // Props for User Status Filter Dropdown
  userStatusFilters: UserStatus[];
  isUserStatusDropdownOpen: boolean;
  setIsUserStatusDropdownOpen: (isOpen: boolean) => void;
  handleUserStatusFilterChange: (value: UserStatus, checked: boolean) => void;
  resetUserStatusFilters: () => void;
  userStatusDropdownRef: React.RefObject<HTMLDivElement | null>;
  // Props for Pipeline Status Filter Dropdown
  pipelineStatusFilters: PipelineStatus[];
  isPipelineStatusDropdownOpen: boolean;
  setIsPipelineStatusDropdownOpen: (isOpen: boolean) => void;
  handlePipelineStatusFilterChange: (
    value: PipelineStatus,
    checked: boolean,
  ) => void;
  resetPipelineStatusFilters: () => void;
  pipelineStatusDropdownRef: React.RefObject<HTMLDivElement | null>;
  // Props for Delete Modal
  handleOpenDeleteModal: (article: MissingArticleItemSchema) => void;
}

const getColumns = ({
  sortBy,
  sortDesc,
  setSortBy,
  setSortDesc,
  setPage,
  userStatusFilters,
  isUserStatusDropdownOpen,
  setIsUserStatusDropdownOpen,
  handleUserStatusFilterChange,
  resetUserStatusFilters,
  userStatusDropdownRef,
  pipelineStatusFilters,
  isPipelineStatusDropdownOpen,
  setIsPipelineStatusDropdownOpen,
  handlePipelineStatusFilterChange,
  resetPipelineStatusFilters,
  pipelineStatusDropdownRef,
  handleOpenDeleteModal,
}: GetColumnsProps): ColumnDef<MissingArticleItemSchema>[] => [
  {
    accessorKey: "title",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc = sortBy === "title" ? !sortDesc : true;
          setSortBy("title");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Item Title
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50 group-hover:opacity-100" />
        {sortBy === "title" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => (
      <Link to={`/missing-articles/item/${row.original._id}`}>
        <div className="flex items-start gap-2">
          <div
            className="tooltip tooltip-right z-10 overflow-visible"
            data-tip={row.original.section_title}
          >
            <div className="mt-0.5 flex-shrink-0">
              {sectionIconMap[row.original.section_title?.toLowerCase() || ""]}
            </div>
          </div>
          <div className="flex-grow">
            <span className="font-medium break-words whitespace-normal">
              {row.original.title_override
                ? row.original.title_override
                : row.original.title}
            </span>
            {row.original.manual_creation ? (
              <p className="mt-1 line-clamp-2 text-xs break-words whitespace-normal text-base-content/70">
                {row.original.description}
              </p>
            ) : (
              <p className="mt-1 line-clamp-2 text-xs break-words whitespace-normal text-base-content/70">
                {row.original.description}
              </p>
            )}
          </div>
        </div>
      </Link>
    ),
    meta: { style: { minWidth: "300px", width: "35%" } },
  },
  {
    accessorKey: "release_note_title",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc =
            sortBy === "release_note_title" ? !sortDesc : true;
          setSortBy("release_note_title");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Release Note
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50 group-hover:opacity-100" />
        {sortBy === "release_note_title" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => (
      <div className="text-sm">
        {row.original.release_note_version ? (
          <a
            href={row.original.release_note_url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1 inline-block link link-hover"
            onClick={(e) => e.stopPropagation()}
            title="View Original Release Note"
          >
            <div className="pointer-events-none badge cursor-default border-none font-mono text-xs shadow-md badge-info">
              {row.original.release_note_version}
            </div>
          </a>
        ) : (
          <div className="pointer-events-none badge cursor-default border-none font-mono text-xs shadow-md badge-info">
            Manual Creation
          </div>
        )}
      </div>
    ),
    meta: { style: { minWidth: "150px", width: "10%" } },
  },
  {
    accessorKey: "user_status",
    header: () => (
      <div className="flex items-center">
        <button
          className="btn -ml-3 btn-ghost btn-sm"
          onClick={() => {
            const newSortDesc = sortBy === "user_status" ? !sortDesc : true;
            setSortBy("user_status");
            setSortDesc(newSortDesc);
            setPage(1);
          }}
        >
          User Status
          <ArrowUpDown className="ml-2 h-3 w-3 opacity-50 group-hover:opacity-100" />
          {sortBy === "user_status" && (sortDesc ? "▼" : "▲")}
        </button>
        <div
          ref={userStatusDropdownRef}
          className={`dropdown dropdown-end ${isUserStatusDropdownOpen ? "dropdown-open" : ""}`}
        >
          <button
            tabIndex={0}
            role="button"
            className="btn ml-1 btn-ghost btn-xs"
            onClick={() =>
              setIsUserStatusDropdownOpen(!isUserStatusDropdownOpen)
            }
          >
            <ListFilter className="h-3 w-3" />
          </button>
          <div
            className="dropdown-content menu z-[2] flex min-w-max flex-col gap-1 rounded-box bg-base-100 p-2 text-base-content shadow backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
          >
            {POSSIBLE_USER_STATUSES.map((value) => (
              <label
                key={value}
                className="label flex cursor-pointer items-center justify-between rounded px-2 py-1 hover:bg-base-content/10"
              >
                <span className="label-text text-xs text-base-content/80">
                  {value}
                </span>
                <input
                  type="checkbox"
                  className="checkbox ml-2 checkbox-xs checkbox-primary"
                  checked={userStatusFilters.includes(value)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleUserStatusFilterChange(value, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </label>
            ))}
            <div className="divider my-0.5"></div>
            <button
              className="btn text-error btn-ghost btn-xs"
              onClick={resetUserStatusFilters}
              title="Reset filters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    ),
    cell: ({ row }) => (
      <div
        className={`badge ${getUserStatusColor(row.original.user_status)} pointer-events-none cursor-default border-none font-mono text-xs shadow-md`}
      >
        {row.original.user_status}
      </div>
    ),
    meta: { style: { minWidth: "150px", width: "12%" } },
  },
  {
    accessorKey: "pipeline_status",
    header: () => (
      <div className="flex items-center">
        <button
          className="btn -ml-3 btn-ghost btn-sm"
          onClick={() => {
            const newSortDesc = sortBy === "pipeline_status" ? !sortDesc : true;
            setSortBy("pipeline_status");
            setSortDesc(newSortDesc);
            setPage(1);
          }}
        >
          AI Status
          <ArrowUpDown className="ml-2 h-3 w-3 opacity-50 group-hover:opacity-100" />
          {sortBy === "pipeline_status" && (sortDesc ? "▼" : "▲")}
        </button>
        <div
          ref={pipelineStatusDropdownRef}
          className={`dropdown dropdown-end ${isPipelineStatusDropdownOpen ? "dropdown-open" : ""}`}
        >
          <button
            tabIndex={0}
            role="button"
            className="btn ml-1 btn-ghost btn-xs"
            onClick={() =>
              setIsPipelineStatusDropdownOpen(!isPipelineStatusDropdownOpen)
            }
          >
            <ListFilter className="h-3 w-3" />
          </button>
          <div
            className="dropdown-content menu z-[2] flex min-w-max flex-col gap-1 rounded-box bg-base-100 p-2 text-base-content shadow backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {POSSIBLE_PIPELINE_STATUSES.map((value) => (
              <label
                key={value}
                className="label flex cursor-pointer items-center justify-between rounded px-2 py-1 hover:bg-base-content/10"
              >
                <span className="label-text text-xs text-base-content/80">
                  {value}
                </span>
                <input
                  type="checkbox"
                  className="checkbox ml-2 checkbox-xs checkbox-primary"
                  checked={pipelineStatusFilters.includes(value)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handlePipelineStatusFilterChange(value, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </label>
            ))}
            <div className="divider my-0.5"></div>
            <button
              className="btn text-error btn-ghost btn-xs"
              onClick={resetPipelineStatusFilters}
              title="Reset filters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    ),
    cell: ({ row }) => (
      <div
        className={`badge ${getPipelineStatusColor(row.original.pipeline_status)} pointer-events-none cursor-default border-none font-mono text-xs shadow-md`}
      >
        {row.original.pipeline_status}
      </div>
    ),
    meta: { style: { minWidth: "150px", width: "12%" } },
  },
  {
    accessorKey: "db_last_updated",
    header: () => (
      <button
        className="btn -ml-3 btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc = sortBy === "db_last_updated" ? !sortDesc : true;
          setSortBy("db_last_updated");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Last Seen
        <ArrowUpDown className="ml-2 h-3 w-3 opacity-50 group-hover:opacity-100" />
        {sortBy === "db_last_updated" && (sortDesc ? "▼" : "▲")}
      </button>
    ),
    cell: ({ row }) => (
      <div className="flex items-center justify-center gap-1 text-xs whitespace-nowrap text-base-content/70">
        <Clock className="h-3 w-3" />
        {formatDistanceToNow(new Date(row.original.db_last_updated), {
          addSuffix: true,
        })}
      </div>
    ),
    meta: {
      style: { minWidth: "130px", width: "11%", textAlign: "right" as const },
    },
  },
  {
    accessorKey: "actions",
    header: () => <div></div>,
    cell: ({ row }) => (
      <div className="flex items-center justify-center">
        <button
          className="hover:bg-opacity-10 btn pt-0 pr-1 pb-0 pl-1 text-error btn-ghost btn-xs hover:bg-error hover:text-error-content"
          onClick={() => handleOpenDeleteModal(row.original)}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    ),
    meta: {
      style: { minWidth: "50px", width: "5%", textAlign: "center" as const },
    },
  },
];

// --- Component ---
const MissingArticles: React.FC<MissingArticlesProps> = ({ apiBaseUrl }) => {
  const [data, setData] = useState<PaginatedMissingArticles | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- State for Pagination, Sorting, Search, Filters ---
  const [page, setPage] = useState<number>(() =>
    parseInt(searchParams.get("page") || "1"),
  );
  const [pageSize, setPageSize] = useState<number>(() =>
    parseInt(searchParams.get("pageSize") || "25"),
  ); // Default 25
  const [sortBy, setSortBy] = useState<string>(
    () => searchParams.get("sortBy") || "db_last_updated",
  );
  const [sortDesc, setSortDesc] = useState<boolean>(
    () => searchParams.get("sortDesc") !== "false",
  );
  const [searchTerm, setSearchTerm] = useState<string>(
    () => searchParams.get("search") || "",
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [userStatusFilters, setUserStatusFilters] = useState<UserStatus[]>(
    () => {
      const saved = localStorage.getItem(LS_USER_STATUS_FILTERS_KEY);
      return saved ? JSON.parse(saved) : [];
    },
  );
  const [pipelineStatusFilters, setPipelineStatusFilters] = useState<
    PipelineStatus[]
  >(() => {
    const saved = localStorage.getItem(LS_PIPELINE_STATUS_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  // Dropdown state and refs
  const [isUserStatusDropdownOpen, setIsUserStatusDropdownOpen] =
    useState(false);
  const userStatusDropdownRef = useRef<HTMLDivElement | null>(null);
  const [isPipelineStatusDropdownOpen, setIsPipelineStatusDropdownOpen] =
    useState(false);
  const pipelineStatusDropdownRef = useRef<HTMLDivElement | null>(null);

  // --- Add Missing Article ---
  // @ts-ignore
  const [missingArticle, setMissingArticle] =
    useState<UserAddedMissingArticle | null>(null);

  // --- Delete Missing Article ---
  const [articleToDelete, setArticleToDelete] =
    useState<MissingArticleItemSchema | null>(null);

  // --- Update URL Search Params & localStorage ---
  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", page.toString());
    params.set("pageSize", pageSize.toString());
    params.set("sortBy", sortBy);
    params.set("sortDesc", sortDesc.toString());
    if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
    if (userStatusFilters.length > 0)
      params.set("user_status", userStatusFilters.join(","));
    if (pipelineStatusFilters.length > 0)
      params.set("pipeline_status", pipelineStatusFilters.join(","));

    setSearchParams(params, { replace: true });

    localStorage.setItem(LS_SORT_BY_KEY, sortBy);
    localStorage.setItem(LS_SORT_DESC_KEY, JSON.stringify(sortDesc));
    localStorage.setItem(LS_SEARCH_TERM_KEY, searchTerm); // Store raw term
    localStorage.setItem(
      LS_USER_STATUS_FILTERS_KEY,
      JSON.stringify(userStatusFilters),
    );
    localStorage.setItem(
      LS_PIPELINE_STATUS_FILTERS_KEY,
      JSON.stringify(pipelineStatusFilters),
    );
  }, [
    page,
    pageSize,
    sortBy,
    sortDesc,
    debouncedSearchTerm,
    userStatusFilters,
    pipelineStatusFilters,
    setSearchParams,
    searchTerm,
  ]);

  // --- Fetch Data ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      sort_by: sortBy,
      sort_desc: sortDesc.toString(),
    });
    if (debouncedSearchTerm) params.set("search_term", debouncedSearchTerm);
    if (userStatusFilters.length > 0)
      params.set("user_status", userStatusFilters.join(","));
    if (pipelineStatusFilters.length > 0)
      params.set("pipeline_status", pipelineStatusFilters.join(","));

    try {
      const response = await axios.get<PaginatedMissingArticles>(
        `${apiBaseUrl}/missing-articles/`,
        { params },
      );
      setData(response.data);
    } catch (err) {
      logger.error("Error fetching missing articles", {
        error: err instanceof Error ? err.message : String(err),
        url: `${apiBaseUrl}/missing-articles`,
        params: Object.fromEntries(params),
      });
      let errorMsg = "Failed to load missing articles.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `Failed to load items: ${err.response.data?.detail || err.message}`;
      } else if (err instanceof Error) {
        errorMsg = `Failed to load items: ${err.message}`;
      }
      setError(errorMsg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    apiBaseUrl,
    page,
    pageSize,
    sortBy,
    sortDesc,
    debouncedSearchTerm,
    userStatusFilters,
    pipelineStatusFilters,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Set Breadcrumbs ---
  useEffect(() => {
    setBreadcrumbs([{ label: "Missing Articles", href: "/missing-articles" }]);
  }, [setBreadcrumbs]);

  const handleAddMissingArticle = async (
    missingArticlePayload: UserAddedMissingArticlePayload,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post<UserAddedMissingArticle>(
        `${apiBaseUrl}/missing-articles/add`,
        missingArticlePayload,
      );
      setMissingArticle(response.data);
      fetchData();
      setError(null);
      setLoading(false);
      handleCloseAddMissingArticle();

      const inputFields = document.querySelectorAll("input, textarea, select");
      inputFields.forEach((field) => {
        if (field instanceof HTMLInputElement) {
          field.value = "";
        } else if (field instanceof HTMLTextAreaElement) {
          field.value = "";
        } else if (field instanceof HTMLSelectElement) {
          field.selectedIndex = 0;
        }
      });
    } catch (err) {
      logger.error("Error adding missing article", {
        error: err instanceof Error ? err.message : String(err),
        url: `${apiBaseUrl}/missing-articles/add`,
        missingArticle: missingArticlePayload,
      });
      let errorMsg = "Failed to add missing article.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `Failed to add missing article: ${err.response.data?.detail || err.message}`;
      } else if (err instanceof Error) {
        errorMsg = `Failed to add missing article: ${err.message}`;
      }
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleCloseAddMissingArticle = useCallback(() => {
    setMissingArticle(null);

    const modal = document.getElementById(
      "add-missing-article-modal",
    ) as HTMLDialogElement;
    modal?.close();
  }, []);

  // --- Delete Missing Article Handlers ---
  const handleOpenDeleteModal = useCallback(
    (article: MissingArticleItemSchema) => {
      setArticleToDelete(article);
      const modal = document.getElementById(
        "delete_missing_article_modal",
      ) as HTMLDialogElement;
      modal?.showModal();
    },
    [],
  );

  const handleDeleteMissingArticle = async (id: string) => {
    try {
      const response = await axios.delete<MissingArticleItemSchema>(
        `${apiBaseUrl}/missing-articles/item/${id}`,
      );
      setArticleToDelete(response.data);
      setError(null);
      setLoading(false);
      handleCloseDeleteModal();

      // Close modal and reset state
      const modal = document.getElementById(
        "delete_missing_article_modal",
      ) as HTMLDialogElement;
      modal?.close();
      setArticleToDelete(null);

      // Wait a moment to allow WebSocket notification to show before refreshing data
      setTimeout(() => {
        fetchData();
      }, 800); // Small delay to let notification appear first
    } catch (error) {
      console.error("Error deleting article:", error);
    }
  };

  const handleCloseDeleteModal = useCallback(() => {
    setArticleToDelete(null);
    const modal = document.getElementById(
      "delete_missing_article_modal",
    ) as HTMLDialogElement;
    modal?.close();
  }, []);

  // --- Filter Handlers ---
  const handleUserStatusFilterChange = useCallback(
    (value: UserStatus, checked: boolean) => {
      setUserStatusFilters((prev) => {
        const newFilters = checked
          ? [...prev, value]
          : prev.filter((v) => v !== value);
        setPage(1);
        return newFilters;
      });
    },
    [setUserStatusFilters, setPage],
  );

  const resetUserStatusFilters = useCallback(() => {
    setUserStatusFilters([]);
    setPage(1);
    setIsUserStatusDropdownOpen(false);
  }, [setUserStatusFilters, setPage]);

  const handlePipelineStatusFilterChange = useCallback(
    (value: PipelineStatus, checked: boolean) => {
      setPipelineStatusFilters((prev) => {
        const newFilters = checked
          ? [...prev, value]
          : prev.filter((v) => v !== value);
        setPage(1);
        return newFilters;
      });
    },
    [setPipelineStatusFilters, setPage],
  );

  const resetPipelineStatusFilters = useCallback(() => {
    setPipelineStatusFilters([]);
    setPage(1);
    setIsPipelineStatusDropdownOpen(false);
  }, [setPipelineStatusFilters, setPage]);

  // --- Close dropdowns on outside click ---
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userStatusDropdownRef.current &&
        !userStatusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsUserStatusDropdownOpen(false);
      }
      if (
        pipelineStatusDropdownRef.current &&
        !pipelineStatusDropdownRef.current.contains(event.target as Node)
      ) {
        setIsPipelineStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Instantiate Columns ---
  const columns = React.useMemo(
    () =>
      getColumns({
        sortBy,
        sortDesc,
        setSortBy,
        setSortDesc,
        setPage,
        userStatusFilters,
        isUserStatusDropdownOpen,
        setIsUserStatusDropdownOpen,
        handleUserStatusFilterChange,
        resetUserStatusFilters,
        userStatusDropdownRef,
        pipelineStatusFilters,
        isPipelineStatusDropdownOpen,
        setIsPipelineStatusDropdownOpen,
        handlePipelineStatusFilterChange,
        resetPipelineStatusFilters,
        pipelineStatusDropdownRef,
        handleOpenDeleteModal,
      }),
    [
      sortBy,
      sortDesc,
      setPage,
      userStatusFilters,
      isUserStatusDropdownOpen,
      handleUserStatusFilterChange,
      resetUserStatusFilters,
      pipelineStatusFilters,
      isPipelineStatusDropdownOpen,
      handlePipelineStatusFilterChange,
      resetPipelineStatusFilters,
      handleOpenDeleteModal,
    ],
  );

  // --- Pagination handler for SimpleTable ---
  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    const newPaginationState =
      typeof updater === "function"
        ? updater({ pageIndex: page - 1, pageSize })
        : updater;

    if (newPaginationState.pageSize !== pageSize) {
      setPageSize(newPaginationState.pageSize);
      setPage(1); // Reset to page 1 on page size change
    } else if (newPaginationState.pageIndex + 1 !== page) {
      setPage(newPaginationState.pageIndex + 1);
    }
  };

  // --- Render ---
  if (loading && !data?.items) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className="loading loading-lg loading-spinner"></span>
        <p className="ml-4 text-base-content/70">Loading missing articles...</p>
      </div>
    );
  }

  if (error && !data?.items) {
    return (
      <div
        role="alert"
        className="mx-auto mt-8 alert max-w-2xl alert-error shadow-lg"
      >
        <AlertTriangle className="h-6 w-6" />
        <div>
          <h3 className="font-bold">Error Loading Data</h3>
          <div className="text-xs">{error}</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={fetchData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-4 flex flex-col items-center justify-between gap-4 md:flex-row">
        <h1 className="text-2xl font-bold">
          Missing Articles ({loading ? "..." : (data?.total_items ?? 0)})
        </h1>
        <div className="flex items-center gap-2">
          <button
            className="btn btn-sm btn-neutral"
            onClick={() => {
              const modal = document.getElementById(
                "add-missing-article-modal",
              ) as HTMLDialogElement;
              modal?.showModal();
            }}
          >
            Add Missing Article
          </button>
          <dialog id="add-missing-article-modal" className="modal">
            <div className="modal-box w-11/12 max-w-3xl py-10">
              <h3 className="px-4 text-lg font-semibold">
                Add Missing Article
              </h3>
              <form
                className="mt-6 flex w-full flex-col gap-4 px-4"
                id="add-missing-article-form"
              >
                <label className="floating-label">
                  <span className="after:ml-1 after:text-error after:content-['*']">
                    Title
                  </span>
                  <input
                    type="text"
                    required
                    placeholder="Title *"
                    className="input input-md w-full rounded-sm focus:outline-none"
                    name="title"
                  />
                </label>

                <label className="floating-label">
                  <span className="after:ml-1 after:text-error after:content-['*']">
                    Description
                  </span>
                  <textarea
                    className="textarea h-48 w-full rounded-sm textarea-md focus:outline-none"
                    placeholder="Description *"
                    name="description"
                  ></textarea>
                </label>
                <div className="flex w-full items-center gap-2">
                  <div className="modal-action -mt-0 w-full items-center justify-between">
                    <select
                      defaultValue="Priority *"
                      required
                      className="select w-1/4 select-md select-none focus:outline-none"
                      name="priority"
                    >
                      <option disabled={true}>Priority *</option>
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                    </select>
                    <select
                      defaultValue="Section *"
                      required
                      className="select w-1/4 select-md select-none focus:outline-none"
                      name="section_title"
                    >
                      <option disabled={true}>Section *</option>
                      <option>Api</option>
                      <option>Allmänna uppdateringar</option>
                      <option>Allmänt</option>
                      <option>Förbättringar för arbetsgivaren</option>
                      <option>Integrationer</option>
                      <option>KBR</option>
                      <option>Volymrekrytering</option>
                      <option>Ärenden</option>
                      <option>Övrigt</option>
                    </select>

                    <div className="flex gap-2">
                      <span
                        className="btn px-4 btn-sm btn-success"
                        onClick={() => {
                          const formData = new FormData(
                            document.getElementById(
                              "add-missing-article-form",
                            ) as HTMLFormElement,
                          );
                          const title = formData.get("title") as string;
                          const description = formData.get(
                            "description",
                          ) as string;
                          const priority = formData.get("priority") as string;
                          const section_title = formData.get(
                            "section_title",
                          ) as string;

                          const payload: UserAddedMissingArticlePayload = {
                            title: title,
                            description: description,
                            priority: priority,
                            section_title: section_title,
                            manual_creation: true,
                          };

                          handleAddMissingArticle(payload);
                        }}
                      >
                        Add
                      </span>
                      <button
                        className="btn px-4 btn-ghost btn-sm"
                        onClick={handleCloseAddMissingArticle}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </dialog>

          {/* Delete Missing Article Modal */}
          <dialog id="delete_missing_article_modal" className="modal">
            <div className="modal-box">
              <h3 className="px-4 pt-4 pb-4 text-lg font-bold">
                Delete Missing Article
              </h3>
              <p className="px-4 pb-4 text-sm">
                Are you sure you want to delete{" "}
                {articleToDelete && (
                  <span className="font-bold">
                    {articleToDelete.title_override || articleToDelete.title}
                  </span>
                )}
                ?
              </p>
              <div className="modal-action">
                <form method="dialog" className="flex gap-2">
                  <button
                    className="hover:bg-opacity-10 btn text-error btn-ghost btn-sm hover:bg-error hover:text-error-content"
                    onClick={() => {
                      if (articleToDelete) {
                        handleDeleteMissingArticle(articleToDelete._id);
                      }
                    }}
                  >
                    Delete
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={handleCloseDeleteModal}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          </dialog>
          <div className="relative w-full md:w-64">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-base-content/40" />
            <input
              type="search"
              placeholder="Search titles, descriptions..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="input-bordered input input-sm w-full pl-9"
            />
          </div>
        </div>
      </div>

      {error && data?.items && (
        <div role="alert" className="mb-4 alert alert-error">
          <AlertTriangle className="h-5 w-5" />
          <span>{error}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Table */}
      {(data?.items && data.items.length > 0) || loading ? (
        <SimpleTable
          columns={columns}
          data={data?.items ?? []}
          isLoading={loading}
          skeletonRowCount={Math.max(10, pageSize / 2)}
          pageCount={data?.total_pages ?? 0}
          pagination={{ pageIndex: page - 1, pageSize }}
          onPaginationChange={handlePaginationChange}
        />
      ) : (
        !loading && (
          <div className="py-10 text-center">
            <Info className="mx-auto mb-3 h-10 w-10 text-base-content/30" />
            <p className="font-semibold">No missing articles found.</p>
            <p className="text-sm text-base-content/60">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default MissingArticles;
